import React, { useEffect, useState } from 'react';
import { Chart } from 'react-google-charts';
import { BalanceFinancialItem } from '@/DTOS/Report/GanttFinancial/BalanceFinancialItem';

interface ChartBalanceProps {
  contracts: BalanceFinancialItem[];
  selectedMonth: Date;
  bankBalance: number | null;
}

const ChartBalance: React.FC<ChartBalanceProps> = ({
  contracts,
  selectedMonth,
  bankBalance,
}) => {
  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const firstDayOfMonth = new Date(
    selectedMonth.getFullYear(),
    selectedMonth.getMonth(),
    1
  );
  const lastDayOfMonth = new Date(
    selectedMonth.getFullYear(),
    selectedMonth.getMonth() + 1,
    0
  );

  const [data, setData] = useState<Array<Array<string | Date>>>([]);

  useEffect(() => {
    const newData: Array<Array<string | Date>> = [];
    let hasPositiveData = false;

    contracts.forEach((balance) => {
      balance.intervals.forEach((interval) => {
        const startDate = new Date(interval.date);
        const endDate = addDays(startDate, 1);

        if (startDate >= firstDayOfMonth && endDate <= lastDayOfMonth) {
          if (interval.price >= 0) {
            hasPositiveData = true;
            newData.push([
              'Total Price',
              `${interval.price}`,
              startDate,
              endDate,
            ]);
          } else {
            newData.push([
              'Total Price (-)',
              `${interval.price}`,
              startDate,
              endDate,
            ]);
          }
        }
      });
    });

    // If no positive data exists, add a row with '0'
    if (!hasPositiveData) {
      newData.unshift(['Total Price', '0', firstDayOfMonth, firstDayOfMonth]);
    }

    // Ensure 'Total Price' row is always first, followed by 'Total Price (-)'
    const orderedData = [
      ...newData.filter((row) => row[0] === 'Total Price'),
      ...newData.filter((row) => row[0] === 'Total Price (-)'),
    ];

    setData([
      ['Label', 'Description', 'Start Date', 'End Date'],
      ...orderedData,
    ]);
  }, [contracts, selectedMonth, bankBalance]);

  const options = {
    timeline: {
      showRowLabels: true,
      avoidOverlappingGridLines: false,
      barLabelStyle: { fontSize: 12 },
      colorByRowLabel: true,
    },
    colors: ['green', 'red'],
    hAxis: {
      format: 'dd MMM',
      minValue: firstDayOfMonth,
      maxValue: lastDayOfMonth,
    },
    height: 150,
  };

  return (
    <Chart
      chartType='Timeline'
      width='100%'
      height={`${options.height}px`}
      data={data}
      options={options}
    />
  );
};

export default ChartBalance;
